import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  Building, 
  Mail, 
  ExternalLink,
  Link as LinkIcon,
  Plus,
  CheckCircle,
  X
} from 'lucide-react';
import { UnmappedSource, CustomerProfile } from '../types/api';
import { SourceType } from '../types/api';
import SlackIcon from './icons/SlackIcon';
import GongIcon from './icons/GongIcon';
import TrelloIcon from './icons/TrelloIcon';
import LinearIcon from './icons/LinearIcon';

interface SourceMappingRowProps {
  source: UnmappedSource;
  customers: CustomerProfile[];
  onMapToExisting: (source: UnmappedSource, customerId: string) => Promise<void>;
  onCreateNew: (source: UnmappedSource) => void;
  onExclude?: (source: UnmappedSource) => Promise<void>;
  loading: boolean;
}

const SourceMappingRow: React.FC<SourceMappingRowProps> = ({
  source,
  customers,
  onMapToExisting,
  onCreateNew,
  onExclude,
  loading
}) => {
  const [showCustomerSelect, setShowCustomerSelect] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>('');
  const [mappingLoading, setMappingLoading] = useState(false);
  const [excludeLoading, setExcludeLoading] = useState(false);
  const [buttonRef, setButtonRef] = useState<HTMLButtonElement | null>(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });

  // Calculate dropdown position when button ref changes
  useEffect(() => {
    if (buttonRef && showCustomerSelect) {
      const rect = buttonRef.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
      const dropdownHeight = 256; // max-h-64 = 256px
      const dropdownWidth = 256; // w-64 = 256px
      
      // Calculate optimal position
      let top = rect.bottom + window.scrollY + 4;
      let left = rect.right - dropdownWidth + window.scrollX;
      
      // Mobile-first responsive positioning
      const isMobile = viewportWidth < 768; // md breakpoint
      
      if (isMobile) {
        // On mobile, center the dropdown and make it full-width with margins
        left = Math.max(window.scrollX + 16, Math.min(left, window.scrollX + viewportWidth - dropdownWidth - 16));
        // Always position below on mobile for better UX
        top = rect.bottom + window.scrollY + 4;
      } else {
        // Desktop positioning logic
        // Check if dropdown would go below viewport
        if (rect.bottom + dropdownHeight > viewportHeight) {
          // Position above the button instead
          top = rect.top + window.scrollY - dropdownHeight - 4;
        }
        
        // Check if dropdown would go off right edge
        if (rect.right - dropdownWidth < 0) {
          left = rect.left + window.scrollX;
        }
        
        // Ensure dropdown doesn't go off left edge
        if (left < window.scrollX) {
          left = window.scrollX + 8; // 8px margin from edge
        }
      }
      
      setDropdownPosition({ top, left });
    }
  }, [buttonRef, showCustomerSelect]);

  const getSourceIcon = (sourceType: SourceType) => {
    switch (sourceType) {
      case 'slack_channel': 
        return (
          <div className="w-6 h-6 rounded-md bg-purple-50 border border-purple-200 flex items-center justify-center">
            <SlackIcon size={16} />
          </div>
        );
      case 'gong_call': 
        return (
          <div className="w-6 h-6 rounded-md bg-orange-50 border border-orange-200 flex items-center justify-center">
            <GongIcon size={16} />
          </div>
        );

      case 'crm_account': 
        return (
          <div className="w-6 h-6 rounded-md bg-blue-50 border border-blue-200 flex items-center justify-center">
            <Building className="w-4 h-4 text-blue-600" />
          </div>
        );
      case 'email_domain': 
        return (
          <div className="w-6 h-6 rounded-md bg-orange-50 border border-orange-200 flex items-center justify-center">
            <Mail className="w-4 h-4 text-orange-600" />
          </div>
        );
      case 'trello_org':
        return (
          <div className="w-6 h-6 rounded-md bg-blue-50 border border-blue-200 flex items-center justify-center">
            <TrelloIcon className="w-4 h-4" />
          </div>
        );
      case 'linear_org':
        return (
          <div className="w-6 h-6 rounded-md bg-indigo-50 border border-indigo-200 flex items-center justify-center">
            <LinearIcon className="w-4 h-4" />
          </div>
        );
      case 'jira_org':
        return (
          <div className="w-6 h-6 rounded-md bg-blue-50 border border-blue-200 flex items-center justify-center">
            <span className="text-[10px] font-bold text-blue-700">J</span>
          </div>
        );
      case 'zendesk_org':
        return (
          <div className="w-6 h-6 rounded-md bg-green-50 border border-green-200 flex items-center justify-center">
            <span className="text-[10px] font-bold text-green-700">Z</span>
          </div>
        );
      default: 
        return (
          <div className="w-6 h-6 rounded-md bg-gray-50 border border-gray-200 flex items-center justify-center">
            <ExternalLink className="w-4 h-4 text-gray-600" />
          </div>
        );
    }
  };

  const getSourceTypeLabel = (sourceType: SourceType) => {
    return sourceType.replace('_', ' ').replace(/\b\w/g, (l: string) => l.toUpperCase());
  };

  const getConfidenceBadgeVariant = (score?: number): "default" | "secondary" | "destructive" | "outline" => {
    if (!score) return 'outline';
    if (score >= 80) return 'default';
    if (score >= 60) return 'secondary';
    return 'destructive';
  };

  const handleMapToExisting = async () => {
    if (!selectedCustomerId) return;
    
    try {
      setMappingLoading(true);
      await onMapToExisting(source, selectedCustomerId);
      setShowCustomerSelect(false);
      setSelectedCustomerId('');
    } catch (error) {
      console.error('Failed to map source:', error);
    } finally {
      setMappingLoading(false);
    }
  };

  const handleExclude = async () => {
    if (!onExclude) return;
    
    const confirmed = window.confirm(
      `Are you sure you want to exclude "${source.source_name}" from future discoveries? This channel will no longer appear in unmapped sources.`
    );
    
    if (!confirmed) return;
    
    try {
      setExcludeLoading(true);
      await onExclude(source);
    } catch (error) {
      console.error('Failed to exclude source:', error);
      alert('Failed to exclude channel. Please try again.');
    } finally {
      setExcludeLoading(false);
    }
  };

  return (
    <Card className="border border-border hover:border-border/80 transition-colors">
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          {/* Source Information */}
          <div className="flex items-center space-x-3 flex-1">
            <div className="flex-shrink-0">
              {getSourceIcon(source.source_type)}
            </div>
            
            <div className="flex-1 min-w-0">
              <div className="flex items-center space-x-2">
                <h4 className="text-sm font-medium text-foreground truncate">
                  {source.source_name}
                </h4>
                <Badge variant="outline" className="text-xs">
                  {getSourceTypeLabel(source.source_type)}
                </Badge>
                {source.confidence_score && (
                  <Badge variant={getConfidenceBadgeVariant(source.confidence_score)} className="text-xs">
                    {source.confidence_score}% confidence
                  </Badge>
                )}
              </div>
              
              {source.suggested_customer_name && (
                <div className="mt-1 flex items-center space-x-2">
                  <CheckCircle className="w-3 h-3 text-green-600" />
                  <span className="text-xs text-green-700 font-medium">
                    Suggested Account: {source.suggested_customer_name}
                  </span>
                </div>
              )}
              
              <div className="mt-1 text-xs text-muted-foreground">
                ID: {source.source_id}
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center space-x-2 ml-4">
            {/* Link to Existing Customer */}
            <div className="relative">
              <Button
                ref={setButtonRef}
                variant="outline"
                size="sm"
                onClick={() => setShowCustomerSelect(!showCustomerSelect)}
                disabled={loading || mappingLoading}
                className="flex items-center space-x-1"
              >
                <LinkIcon className="w-3 h-3" />
                <span>Link to Account</span>
              </Button>

              {showCustomerSelect && createPortal(
                <div 
                  className="fixed w-64 md:w-64 sm:w-80 bg-background border border-border rounded-lg shadow-xl z-[100] max-h-80 overflow-y-auto"
                  style={{
                    top: dropdownPosition.top,
                    left: dropdownPosition.left
                  }}
                >
                  <div className="p-2">
                    <div className="text-xs font-medium text-muted-foreground mb-2">
                      Select Account to Link:
                    </div>
                    
                    {customers.length === 0 ? (
                      <div className="p-2 text-sm text-muted-foreground text-center">
                        No accounts available
                      </div>
                    ) : (
                      <div className="space-y-1 max-h-48 overflow-y-auto">
                        {customers.map((customer) => (
                          <button
                            key={customer.customer_id}
                            className={`w-full text-left p-2 rounded text-sm hover:bg-accent transition-colors ${
                              selectedCustomerId === customer.customer_id 
                                ? 'bg-primary/10 border border-primary/20' 
                                : ''
                            }`}
                            onClick={() => setSelectedCustomerId(customer.customer_id)}
                          >
                            <div className="font-medium truncate text-foreground">{customer.name}</div>
                            <div className="text-xs text-muted-foreground truncate">
                              {customer.domains.length > 0 ? customer.domains[0] : 'No domains'}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                    
                    {selectedCustomerId && (
                      <div className="mt-3 pt-2 border-t border-border bg-muted/50 -mx-2 px-2 pb-2">
                        <Button
                          size="sm"
                          onClick={handleMapToExisting}
                          disabled={mappingLoading}
                          className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-medium"
                        >
                          {mappingLoading ? (
                            <div className="flex items-center justify-center space-x-2">
                              <div className="w-3 h-3 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" />
                              <span>Linking...</span>
                            </div>
                          ) : (
                            <div className="flex items-center justify-center space-x-1">
                              <CheckCircle className="w-3 h-3" />
                              <span>Confirm Link</span>
                            </div>
                          )}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>,
                document.body
              )}
            </div>

            {/* Create New Customer */}
            <Button
              size="sm"
              onClick={() => onCreateNew(source)}
              disabled={loading || mappingLoading || excludeLoading}
              className="flex items-center space-x-1"
            >
              <Plus className="w-3 h-3" />
              <span>Create New</span>
            </Button>

            {/* Exclude Channel (only for Slack channels) */}
            {onExclude && source.source_type === 'slack_channel' && (
              <Button
                size="sm"
                variant="ghost"
                onClick={handleExclude}
                disabled={loading || mappingLoading || excludeLoading}
                className="flex items-center space-x-1 text-destructive hover:text-destructive hover:bg-destructive/10"
              >
                {excludeLoading ? (
                  <div className="w-3 h-3 border-2 border-destructive border-t-transparent rounded-full animate-spin" />
                ) : (
                  <X className="w-3 h-3" />
                )}
                <span>{excludeLoading ? 'Excluding...' : 'Exclude'}</span>
              </Button>
            )}
          </div>
        </div>

        {/* Customer Selection Overlay */}
        {showCustomerSelect && createPortal(
          <div 
            className="fixed inset-0 z-[90]" 
            onClick={() => setShowCustomerSelect(false)}
          />,
          document.body
        )}
      </CardContent>
    </Card>
  );
};

export default SourceMappingRow;
